import { Path, UseFieldArrayRemove, useFormContext } from 'react-hook-form';
import { Control } from 'react-hook-form';
import {
    IJobQuestionsForm,
    ISaveJobForm,
    JobQuestionObjectFields,
} from '../../../../../common';
import { useSortable } from '@dnd-kit/sortable';
import {
    StyledQuestionPaper,
    StyledSortableQuestionWrapper,
    StyledQuestionMainContent,
} from '../JobQuestions.styles';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '../../../../../base';
import { useTranslation } from 'react-i18next';
import {
    useQuestionFormState,
    useQuestionOptions,
    useDealbreakerLogic,
    useOptionsErrors,
} from '../hooks';
import { QUESTION_TYPE_OPTIONS } from '../constants';
import {
    QuestionHeader,
    QuestionMainFields,
    QuestionOptions,
    QuestionBooleanOptions,
    QuestionActionButtons,
    QuestionTextEditor,
    QuestionShortText,
    QuestionFileUpload,
    QuestionDatePicker,
    QuestionNumericInput,
} from './components';

interface Props {
    fieldId: string;
    index: number;
    control: Control<IJobQuestionsForm>;
    remove: UseFieldArrayRemove;
}

const SortableQuestion = ({ fieldId, index, control, remove }: Props) => {
    const { t } = useTranslation();

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: fieldId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : 'auto',
    };

    const { setValue } = useFormContext<ISaveJobForm>();

    const {
        fieldNamePrefix,
        isDealbreaker,
        questionTypeValue,
        shouldShowDealbreaker,
        shouldShowOptions,
        shouldShowBooleanOptions,
        shouldShowTextEditor,
        shouldShowShortText,
        shouldShowFileUpload,
        shouldShowDatePicker,
        isDateRangeType,
        shouldShowNumeric,
        hasBeenSubmitted,
        hasQuestionTitleError,
        optionsError,
        dealbreakerAnswerError,
    } = useQuestionFormState({ control, index });

    const handleDealbreakerToggle = (checked: boolean) => {
        if (checked) {
            setValue(
                `${fieldNamePrefix}.${JobQuestionObjectFields.IS_REQUIRED}` as Path<ISaveJobForm>,
                true as never,
                { shouldDirty: true }
            );
        }
    };

    const { optionFields, removeOption, handleAddOption } = useQuestionOptions({
        control,
        fieldNamePrefix,
    });

    const {
        handleDealbreakerAnswerChange,
        handleBooleanDealbreakerChange,
    } = useDealbreakerLogic({
        control,
        index,
        fieldNamePrefix,
        questionType: questionTypeValue,
    });

    const { noOptionsErrorMessage } = useOptionsErrors({
        hasBeenSubmitted,
        optionFieldsLength: optionFields.length,
        optionsError,
    });

    const handleCopyQuestion = () => {
        // TODO: Implement copy functionality
        console.log('Copy question:', index);
    };

    const handleDeleteQuestion = () => {
        remove(index);
    };

    const questionTypeOptions = QUESTION_TYPE_OPTIONS(t);

    return (
        <StyledSortableQuestionWrapper ref={setNodeRef} style={style}>
            <StyledQuestionPaper variant="outlined">
                <QuestionHeader
                    attributes={attributes}
                    listeners={listeners}
                    onCopy={handleCopyQuestion}
                    onDelete={handleDeleteQuestion}
                />

                <StyledQuestionMainContent>
                    <QuestionMainFields
                        fieldNamePrefix={fieldNamePrefix}
                        control={control}
                        hasBeenSubmitted={hasBeenSubmitted}
                        hasQuestionTitleError={!!hasQuestionTitleError}
                        shouldShowDealbreaker={shouldShowDealbreaker}
                        questionTypeOptions={questionTypeOptions}
                        onDealbreakerChange={handleDealbreakerToggle}
                    />

                    <QuestionActionButtons
                        onCopy={handleCopyQuestion}
                        onDelete={handleDeleteQuestion}
                    />
                </StyledQuestionMainContent>

                {shouldShowOptions && (
                    <Box>
                        <QuestionOptions
                            fieldNamePrefix={fieldNamePrefix}
                            control={control}
                            optionFields={optionFields}
                            isDealbreaker={!!isDealbreaker}
                            hasBeenSubmitted={hasBeenSubmitted}
                            optionsError={optionsError}
                            noOptionsErrorMessage={noOptionsErrorMessage}
                            questionType={questionTypeValue}
                            onAddOption={handleAddOption}
                            onRemoveOption={removeOption}
                            onDealbreakerAnswerChange={
                                handleDealbreakerAnswerChange
                            }
                        />
                    </Box>
                )}

                {shouldShowBooleanOptions && (
                    <Box>
                        <QuestionBooleanOptions
                            fieldNamePrefix={fieldNamePrefix}
                            control={control}
                            isDealbreaker={!!isDealbreaker}
                            hasBeenSubmitted={hasBeenSubmitted}
                            dealbreakerAnswerError={dealbreakerAnswerError}
                            onDealbreakerAnswerChange={
                                handleBooleanDealbreakerChange
                            }
                        />
                    </Box>
                )}

                {shouldShowTextEditor && (
                    <Box>
                        <QuestionTextEditor />
                    </Box>
                )}

                {shouldShowShortText && (
                    <Box>
                        <QuestionShortText />
                    </Box>
                )}

                {shouldShowFileUpload && (
                    <Box>
                        <QuestionFileUpload />
                    </Box>
                )}

                {shouldShowDatePicker && (
                    <Box>
                        <QuestionDatePicker
                            isDateRange={isDateRangeType}
                            isDealbreaker={!!isDealbreaker}
                            fieldNamePrefix={fieldNamePrefix}
                            control={control}
                            index={index}
                            hasBeenSubmitted={hasBeenSubmitted}
                        />
                    </Box>
                )}

                {shouldShowNumeric && (
                    <Box>
                        <QuestionNumericInput
                            fieldNamePrefix={fieldNamePrefix}
                            control={control}
                            index={index}
                            isDealbreaker={!!isDealbreaker}
                            hasBeenSubmitted={hasBeenSubmitted}
                        />
                    </Box>
                )}
            </StyledQuestionPaper>
        </StyledSortableQuestionWrapper>
    );
};

export default SortableQuestion;
